import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams } from "react-router-dom";

import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';
import PageHeader from '../partials/PageHeader';

import { Text, Small, Panel, H4, Badge, Box } from '@bigcommerce/big-design';
import { warehouseReceivingOrdersIndex } from '../../redux/actions/receivingOrders';

const WarehouseReceivingOrders = ({ payload, loadWarehouseReceivingOrders, receivingOrders, bigcommerceSettings }) => {
  const { id } = useParams();

  useEffect(() => {
    loadWarehouseReceivingOrders(payload, id);
  }, []);

  const format = (value) => {
    let formattedValue = "";

    if(bigcommerceSettings.currency_symbol_location === 'left') {
      formattedValue += bigcommerceSettings.currency_symbol
    }
    
    formattedValue += parseFloat(value).toFixed(bigcommerceSettings.decimal_places)

    if(bigcommerceSettings.currency_symbol_location !== 'left') {
      formattedValue += bigcommerceSettings.currency_symbol
    }

    formattedValue = formattedValue.replace('.', bigcommerceSettings.decimal_separator)

    return formattedValue;
  }

  const description = () => {
    return(
      <> 
        <Text>View the list of warehouse receiving orders for this purchase order.</Text>
        <Text>As the purchase order gets received, more receiving orders will appear in this list.</Text>
        <Small>Have questions or need some help? Contact us at <a href="mailto:support@athousandapps.com">support@athousandapps.com</a></Small>
      </>
    )
  }

  return(
    <>
      <PageHeader stripeBackground='#c66628' helpLink="bigcommerce/create-a-warehouse-receiving-order/" />
      <SettingsPage title="Warehouse Receiving Orders" description={description()}>
        <Box
          backgroundColor="primary10"
          border="box"
          borderRadius="normal"
          padding="medium"
        >
          <Text>Labels and barcode labels can be generated for items in received orders with the <a href="https://apps.bigcommerce.com/details/50743" target="_blank">Barcode Inventory Management</a> app.</Text>
          <Text>Warehouse Receiving Orders are automatically sent to the Barcode Inventory Managment app and can be printed in the <a href="https://athousandapps.com/bigcommerce/print-warehouse-receiving-order-labels/" target="_blank">Print from Warehouse Receiving Orders</a> tool.</Text>
        </Box>

        <br/>
        <br/>

        <>
          { (receivingOrders.status !== 'success') &&
            <Panel>
              <LoadingSpinner statusText="Loading Warehouse Receiving Orders" />
            </Panel>
          }


          { (receivingOrders.status === 'success') && receivingOrders?.receivingOrders?.warehouse_receiving_orders?.map(warehouseReceivingOrder => 
            <Panel>
              <div className="flex-container">
                <div className="flex-1">
                  <H4>
                    <strong>WRO ID:</strong> { warehouseReceivingOrder.id }
                  </H4>
                  <H4>
                    <strong>Received Date:</strong> { warehouseReceivingOrder.received_at }
                  </H4>
                </div>
                <div className="flex-1 text-right">
                  <Text>
                    <Text as="span" bold>
                      BigCommerce Update Processing: &nbsp;&nbsp;
                    </Text>
                  { !warehouseReceivingOrder.ticket &&
                    <Text as="span" color="warning40">
                      No processing recorded
                    </Text>
                  }
                  { warehouseReceivingOrder?.ticket?.transaction_id &&
                    <Text as="span" color="success40">
                      Successfully processed
                    </Text>
                  }
                  { warehouseReceivingOrder?.ticket?.errors &&
                    <Text as="span" color="danger40">
                      Processing failed
                    </Text>
                  }
                  </Text>
                </div>
              </div>

              { warehouseReceivingOrder.warehouse_receiving_order_lines.warehouse_receiving_order_lines.map(warehouseReceivingOrderLine =>
                <div className="has-hover sku-line flex-container">
                  <div className="flex-container">
                    <div className="left-15">
                      { warehouseReceivingOrderLine.purchase_order_line.source_image_url && <img src={warehouseReceivingOrderLine.purchase_order_line.source_image_url} /> }
                      { !warehouseReceivingOrderLine.purchase_order_line.source_image_url && <Text>No image</Text>}
                    </div>
                  </div>

                  <div className="flex-2">
                    <div className="left-15">
                      <H4>Line Details</H4>
                      <ul>
                        <li>Supplier SKU: <Badge label={warehouseReceivingOrderLine.purchase_order_line.supplier_sku || "Not set"} variant={warehouseReceivingOrderLine.purchase_order_line.supplier_sku ? "success" : "warning"} /></li>
                        <li>Supplier Price: <Badge label={warehouseReceivingOrderLine.purchase_order_line.supplier_price ? format(warehouseReceivingOrderLine.purchase_order_line.supplier_price) : "Not set"} variant={warehouseReceivingOrderLine.purchase_order_line.supplier_price ? "success" : "warning"} /></li>
                        { (parseInt(warehouseReceivingOrderLine.pack_quantity) > 1) &&
                          <li>
                            Pack Quantity: <Badge label={warehouseReceivingOrderLine.pack_quantity} variant="secondary" />
                          </li>
                        }
                      </ul>
                    </div>
                  </div>

                  <div className="flex-2">
                    <div className="flex-container">
                      <H4>{warehouseReceivingOrderLine.purchase_order_line.source_name}</H4>
                    </div>
                    <ul>
                      { warehouseReceivingOrderLine.purchase_order_line.source_options && warehouseReceivingOrderLine.purchase_order_line.source_options.map(option =>
                          <li>
                            <strong>{ option.option_display_name || option.display_name }</strong>: { option.label || option.display_value }
                          </li>
                        )
                      }
                      { !warehouseReceivingOrderLine.purchase_order_line.source_options &&
                        <Text color="warning40">
                          Base Product
                        </Text>
                      }
                    </ul>

                  </div>
                  <div className="flex-1">
                    <H4>Received Count</H4>
                    { warehouseReceivingOrderLine.count }
                    { (parseInt(warehouseReceivingOrderLine.pack_quantity) > 1) &&
                      <>
                        <Text>x {warehouseReceivingOrderLine.pack_quantity} ({parseInt(warehouseReceivingOrderLine.pack_quantity) * warehouseReceivingOrderLine.count } total)</Text>
                      </>
                    }
                  </div>
                </div>
              )}
            </Panel>
          )}
        </>
      </SettingsPage>
    </>
  )
}

const mapStateToProps = state => ({
  receivingOrders: state.receivingOrders,
  bigcommerceSettings: state.stores.settings,
});

const mapDispatchToProps = (dispatch) => ({
  loadWarehouseReceivingOrders: (payload, purchaseOrderId) => dispatch(warehouseReceivingOrdersIndex(payload, purchaseOrderId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WarehouseReceivingOrders);
